import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { GeocodedAddress } from '../../types/address';

interface NeighborhoodSelectProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (neighborhood: GeocodedAddress | null) => void;
  selectedNeighborhood: GeocodedAddress | null;
  suggestions: GeocodedAddress[];
  isLoading: boolean;
  error?: string | null;
  disabled?: boolean;
}

export function NeighborhoodSelect({
  value,
  onChange,
  onSelect,
  selectedNeighborhood,
  suggestions,
  isLoading,
  error,
  disabled = false
}: NeighborhoodSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedNeighborhood && suggestions.length > 0 && value.length >= 2) {
      setIsOpen(true);
      setActiveIndex(-1);
    } else if (suggestions.length === 0) {
      setIsOpen(false);
    }
  }, [suggestions, value, selectedNeighborhood]);

  const handleSelectNeighborhood = useCallback((neighborhood: GeocodedAddress) => {
    onSelect(neighborhood);
    setIsOpen(false);
    setActiveIndex(-1);
  }, [onSelect]);

  const handleClear = () => {
    onSelect(null);
    onChange('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && suggestions.length > 0 && e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(0);
      return;
    }
    if (!isOpen || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) handleSelectNeighborhood(suggestions[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const listboxId = 'neighborhood-suggestions-listbox';
  const hasError = !!error;

  if (selectedNeighborhood) {
    const parts = selectedNeighborhood.display_name.split(',').map(p => p.trim());
    const mainName = parts[0] || selectedNeighborhood.display_name;
    const locationParts = parts.slice(-2).join(', ');

    return (
      <div ref={containerRef}>
        <label className="block text-[15px] font-semibold mb-2">Barrio</label>
        <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border-2 border-green-500 rounded-lg">
          <div className="shrink-0 w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <span className="block text-[15px] font-medium text-green-800 break-words">{mainName}</span>
            {parts.length > 1 && (
              <span className="block text-[13px] text-green-600 mt-0.5">{locationParts}</span>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-black/5 hover:text-red-500 transition-all"
              aria-label="Cambiar barrio"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor="neighborhood-input" className="block text-[15px] font-semibold mb-2">
        Barrio
        <span className="text-gray-400 font-normal ml-2 text-sm">(Busca por nombre)</span>
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          id="neighborhood-input"
          type="text"
          name="neighborhood"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          disabled={disabled}
          autoComplete="address-level3"
          aria-autocomplete="list"
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={activeIndex >= 0 ? `neighborhood-${activeIndex}` : undefined}
          aria-expanded={isOpen}
          aria-invalid={hasError}
          aria-describedby={hasError ? 'neighborhood-error' : undefined}
          className={`w-full px-4 py-3 text-base border-2 rounded-lg outline-none transition-colors pr-10 ${
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
              : 'border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200'
          }`}
          placeholder="Ej: Las Delicias, Jardin Claret..."
        />

        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <svg className="w-5 h-5 text-green-500 animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          )}
        </div>
      </div>

      {hasError && (
        <p id="neighborhood-error" className="mt-2 text-sm text-red-600" role="alert">{error}</p>
      )}

      {isOpen && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Sugerencias de barrios"
          className="absolute z-[1000] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[280px] overflow-y-auto list-none p-0 m-0"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.lat}-${suggestion.lon}-${index}`}
              id={`neighborhood-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => handleSelectNeighborhood(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`px-4 py-3.5 cursor-pointer transition-colors ${
                index !== suggestions.length - 1 ? 'border-b border-gray-100' : ''
              } ${index === activeIndex ? 'bg-green-50' : 'bg-transparent'}`}
            >
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <div>
                  <p className="m-0 font-medium text-gray-800 text-[15px]">{suggestion.display_name}</p>
                  {suggestion.address?.city && (
                    <p className="m-0 mt-1 text-[13px] text-gray-500">{suggestion.address.city}</p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {isOpen && !isLoading && suggestions.length === 0 && value.length >= 2 && (
        <div className="absolute z-[1000] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-5 text-center">
          <p className="m-0 text-gray-500 text-[15px]">No encontramos barrios con ese nombre</p>
        </div>
      )}
    </div>
  );
}
